import * as React from 'react';
import { BsBarChartSteps } from 'react-icons/bs';
import { Combobox } from '@headlessui/react';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { SortDirection } from 'utils/shared/trades/types';
import classNames from 'styles/utils/classNames';
import SectionTitle from 'components/SectionTitle';
import {
  TableWrapper,
  TableCell,
  TableHeader,
  TableBody,
  HeaderCellWrapper,
  HeaderCell,
  HeaderIcon,
  IconSortDesc,
  IconSortAsc,
} from 'components/Table';
import PulseLoader from 'components/Loading/Pulse';
import {
  useGetDarkPoolLevelsQuery,
  useGetDarkPoolLevelsMonthlyQuery,
  GetDarkPoolLevelsQuery,
} from 'types/generated/client';
import {
  LevelProps,
  LevelsHeaderProps,
  LEVELS_TABLE_HEADERS,
  DEFAULT_LEVELS_KEY,
  LEVELS_TIMEFRAME_OPTIONS,
  DEFAULT_LEVELS_TIMEFRAME,
  LevelsTimeframe,
} from './props';
import TimeframeToggle from './TimeframeToggle';

import { AggregateRow, RowBackground } from './styles';

const STRENGTH_OPTIONS = Array.from({ length: 21 }, (_, i) => i * 5);

const Header: React.FC<React.PropsWithChildren<LevelsHeaderProps>> = React.memo(
  ({
    aggregateSortKey,
    setAggregateSortKey,
    aggregateSortDirection,
    setAggregateSortDirection,
    ticker,
    minStrength,
    setMinStrength,
    query,
    setQuery,
    timeframe,
    setTimeframe,
  }) => {
    const filteredOptions = React.useMemo(() => {
      if (!query) return STRENGTH_OPTIONS;
      const num = parseInt(query, 10);
      const matching = STRENGTH_OPTIONS.filter((opt) => opt.toString().startsWith(query));
      if (!isNaN(num) && num >= 0 && num <= 100 && !matching.includes(num)) {
        return [num, ...matching];
      }
      return matching;
    }, [query]);

    return (
      <TableHeader>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <SectionTitle
              Icon={BsBarChartSteps}
              title={`${ticker} Dark Pool Levels`}
              style={{ marginBottom: 0 }}
            />
            <TimeframeToggle
              value={timeframe}
              onChange={setTimeframe}
              options={LEVELS_TIMEFRAME_OPTIONS}
            />
          </div>
          <Combobox
            as="div"
            value={minStrength}
            onChange={(val: number) => {
              if (val != null) setMinStrength(val);
            }}
            className="relative flex items-center gap-2"
          >
            <span className="rounded-sm border-l-2 border-darkpool pl-1.5 text-xs text-white">
              Min Strength
            </span>
            <Combobox.Input
              className="h-[28px] w-[60px] rounded border border-palette-border bg-palette-black-2 px-2 py-1 text-xs font-medium text-white outline-none ring-palette-border focus:ring-brand-primary"
              onChange={(e) => setQuery(e.target.value)}
              displayValue={(val: number) => String(val)}
              autoComplete="off"
            />
            <Combobox.Options className="absolute right-0 top-full z-10 mt-1 max-h-48 w-[60px] overflow-auto rounded border border-palette-border bg-palette-black-1 py-1 text-xs shadow-lg focus:outline-none">
              {filteredOptions.map((opt) => (
                <Combobox.Option
                  key={opt}
                  value={opt}
                  className={({ active }) =>
                    classNames(
                      'cursor-pointer select-none px-2 py-1',
                      active ? 'bg-brand-primary text-white' : 'text-white',
                    )
                  }
                >
                  {opt}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </Combobox>
        </div>
        <AggregateRow style={{ paddingRight: '4px' }}>
          {LEVELS_TABLE_HEADERS.map((t) => {
            const isActiveSort =
              t.sortKey === aggregateSortKey && aggregateSortDirection !== SortDirection.None;
            return (
              <HeaderCellWrapper
                key={t.name}
                isActiveSort={isActiveSort}
                onClick={() => {
                  if (aggregateSortKey !== t.sortKey) {
                    setAggregateSortKey(t.sortKey);
                    setAggregateSortDirection(SortDirection.Desc);
                  } else if (
                    aggregateSortKey === t.sortKey &&
                    aggregateSortDirection === SortDirection.Desc
                  ) {
                    setAggregateSortDirection(SortDirection.Asc);
                  } else if (
                    aggregateSortKey === t.sortKey &&
                    aggregateSortDirection === SortDirection.Asc
                  ) {
                    setAggregateSortKey(DEFAULT_LEVELS_KEY);
                    setAggregateSortDirection(SortDirection.None);
                  }
                }}
              >
                <HeaderCell>
                  <span className="cell-name">{t.name}</span>
                  {isActiveSort && aggregateSortDirection === SortDirection.Desc && (
                    <HeaderIcon>
                      <IconSortDesc />
                    </HeaderIcon>
                  )}
                  {isActiveSort && aggregateSortDirection === SortDirection.Asc && (
                    <HeaderIcon>
                      <IconSortAsc />
                    </HeaderIcon>
                  )}
                </HeaderCell>
              </HeaderCellWrapper>
            );
          })}
        </AggregateRow>
      </TableHeader>
    );
  },
);

const Row: React.FC<
  React.PropsWithChildren<{
    item: GetDarkPoolLevelsQuery['dark_pool_levels'][0] & { strength: number };
    maxLevel: number;
  }>
> = ({ item }) => {
  return (
    <div className="relative w-full">
      <RowBackground className="absolute left-0 top-0 h-full" strength={item.strength} />
      <AggregateRow
        key={item.price}
        strength={item.strength}
        tableBodyRow
        className="relative px-4"
      >
        <TableCell>{item.price}</TableCell>
        <TableCell>{item.prints}</TableCell>
        <TableCell>{abbreviateNumber(item.volume)}</TableCell>
        <TableCell>${abbreviateNumber(item.amount)}</TableCell>
        <TableCell>{Math.round(item.strength)}</TableCell>
      </AggregateRow>
    </div>
  );
};

const DarkPoolLevelsTable: React.FC<React.PropsWithChildren<LevelProps>> = ({ ticker }) => {
  const [aggregateSortKey, setAggregateSortKey] = React.useState(DEFAULT_LEVELS_KEY);
  const [aggregateSortDirection, setAggregateSortDirection] = React.useState(SortDirection.None);
  const [minStrength, setMinStrength] = React.useState(10);
  const [query, setQuery] = React.useState('');
  const [timeframe, setTimeframe] = React.useState<string>(DEFAULT_LEVELS_TIMEFRAME);
  const isMonthly = timeframe === LevelsTimeframe.Month;
  const { data: weekData, loading: weekLoading } = useGetDarkPoolLevelsQuery({
    variables: { ticker },
    skip: isMonthly,
  });
  const { data: monthData, loading: monthLoading } = useGetDarkPoolLevelsMonthlyQuery({
    variables: { ticker },
    skip: !isMonthly,
  });
  const loading = isMonthly ? monthLoading : weekLoading;
  const levels: Omit<GetDarkPoolLevelsQuery['dark_pool_levels'][0], '__typename'>[] = isMonthly
    ? monthData?.dark_pool_levels_monthly ?? []
    : weekData?.dark_pool_levels ?? [];

  const groupedLevels = React.useMemo(() => {
    const groupedByLevel: Record<string, GetDarkPoolLevelsQuery['dark_pool_levels'][0]> = {};

    levels.forEach((level) => {
      const price = level.price.toFixed(2);
      if (!groupedByLevel[price]) {
        groupedByLevel[price] = {
          ticker: level.ticker,
          amount: 0,
          prints: 0,
          price: price,
          volume: 0,
        };
      }
      groupedByLevel[price].amount += level.amount;
      groupedByLevel[price].prints += level.prints;
      groupedByLevel[price].volume += level.volume;
    });

    return Object.values(groupedByLevel);
  }, [levels, aggregateSortKey, aggregateSortDirection]);
  const maxLevel = React.useMemo(() => {
    let max = 1;

    for (let i = 0; i < groupedLevels.length; i++) {
      if (groupedLevels[i].volume > max) {
        max = groupedLevels[i].volume;
      }
    }

    return max;
  }, [groupedLevels]);
  const levelsWithStrength = React.useMemo(() => {
    return groupedLevels.map((level) => ({
      ...level,
      strength: ((level.volume || 0) / maxLevel) * 100,
    }));
  }, [maxLevel, groupedLevels]);
  const filteredLevels = React.useMemo(() => {
    return levelsWithStrength.filter((level) => level.strength > minStrength);
  }, [levelsWithStrength, minStrength]);
  const displayLevels = React.useMemo(() => {
    const sortedlevels = filteredLevels.sort((a, b) => {
      let first = aggregateSortDirection === SortDirection.Asc ? a : b;
      let last = aggregateSortDirection === SortDirection.Asc ? b : a;

      // @ts-ignore
      if (first[aggregateSortKey] < last[aggregateSortKey]) {
        return -1;
      }
      // @ts-ignore
      if (first[aggregateSortKey] > last[aggregateSortKey]) {
        return 1;
      }
      return 0;
    });

    return sortedlevels;
  }, [filteredLevels, aggregateSortKey, aggregateSortDirection]);

  return (
    <TableWrapper className="min-w-0 max-w-full">
      <Header
        ticker={ticker}
        aggregateSortKey={aggregateSortKey}
        setAggregateSortKey={setAggregateSortKey}
        aggregateSortDirection={aggregateSortDirection}
        setAggregateSortDirection={setAggregateSortDirection}
        minStrength={minStrength}
        setMinStrength={setMinStrength}
        query={query}
        setQuery={setQuery}
        timeframe={timeframe}
        setTimeframe={setTimeframe}
      />
      <TableBody>
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <PulseLoader />
          </div>
        ) : displayLevels?.length ? (
          displayLevels.map((item) => {
            return <Row key={item.price} item={item} maxLevel={maxLevel} />;
          })
        ) : (
          <div className="flex h-full items-center justify-center text-2xl text-gray-400">
            No levels around current price
          </div>
        )}
      </TableBody>
    </TableWrapper>
  );
};

export default React.memo(DarkPoolLevelsTable);
